import {
    Autocomplete,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useProdutos } from "../../hooks/useProdutos";
import { IImpostoProduto } from "../../interfaces/IImpostoProduto";
import { INcm } from "../../interfaces/INcm";
import { ICfop } from "../../interfaces/ICfop";
import {
    CFOPS_SUGERIDOS,
    IMPOSTO_PADRAO_PRODUCAO_PROPRIA,
    NCMS_SUGERIDOS,
    OPCOES_ICMS_MODALIDADE_BASE_CALCULO,
    OPCOES_ICMS_ORIGEM,
} from "../../constants/imposto";

const dedupPorCodigo = <T extends { codigo: string }>(itens: T[]): T[] => {
    const mapa = new Map<string, T>();
    itens.forEach((item) => {
        if (item?.codigo && !mapa.has(item.codigo)) {
            mapa.set(item.codigo, item);
        }
    });
    return Array.from(mapa.values());
};

interface FormularioImpostoProps {
    imposto: IImpostoProduto | undefined;
    onChange: (imposto: IImpostoProduto) => void;
    /** Aplica o modelo base sugerido quando não há imposto definido (produção própria). */
    aplicarPadraoSeVazio?: boolean;
}

export const FormularioImposto = ({ imposto, onChange, aplicarPadraoSeVazio = true }: FormularioImpostoProps) => {
    const { buscarNcms, buscarCfops } = useProdutos();

    // Mescla o padrão com o que já existe: campos não informados (inclusive em produtos
    // antigos salvos antes desses campos) aparecem pré-preenchidos com o modelo base.
    const impostoAtual: IImpostoProduto = { ...IMPOSTO_PADRAO_PRODUCAO_PROPRIA, ...imposto };

    const [opcoesNcm, setOpcoesNcm] = useState<INcm[]>(NCMS_SUGERIDOS);
    const [inputNcm, setInputNcm] = useState("");
    const [buscandoNcm, setBuscandoNcm] = useState(false);
    const debounceNcmRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [opcoesCfop, setOpcoesCfop] = useState<ICfop[]>(CFOPS_SUGERIDOS);
    const [inputCfop, setInputCfop] = useState("");
    const [buscandoCfop, setBuscandoCfop] = useState(false);
    const debounceCfopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Torna o modelo base explícito quando o produto ainda não tem imposto.
    useEffect(() => {
        if (aplicarPadraoSeVazio && !imposto) {
            onChange(IMPOSTO_PADRAO_PRODUCAO_PROPRIA);
        }
    }, []);

    const atualizar = (campo: keyof IImpostoProduto, valor: string | number | undefined) => {
        onChange({ ...impostoAtual, [campo]: valor });
    };

    // ── NCM ──
    const ncmSelecionado: INcm | null = useMemo(() => {
        if (!impostoAtual.ncm) return null;
        return opcoesNcm.find((o) => o.codigo === impostoAtual.ncm) ?? { codigo: impostoAtual.ncm, descricao: "" };
    }, [impostoAtual.ncm, opcoesNcm]);

    const opcoesNcmExibidas: INcm[] = useMemo(
        () => dedupPorCodigo([...(ncmSelecionado ? [ncmSelecionado] : []), ...opcoesNcm]),
        [ncmSelecionado, opcoesNcm]
    );

    const handleInputNcm = (_event: React.SyntheticEvent, valor: string) => {
        setInputNcm(valor);
        if (debounceNcmRef.current) clearTimeout(debounceNcmRef.current);

        const termo = valor.trim();
        if (termo.length < 2) {
            setOpcoesNcm(dedupPorCodigo([...NCMS_SUGERIDOS, ...(ncmSelecionado ? [ncmSelecionado] : [])]));
            return;
        }

        const somenteDigitos = /^\d+$/.test(termo);
        setBuscandoNcm(true);
        debounceNcmRef.current = setTimeout(async () => {
            const resultado = await buscarNcms(somenteDigitos ? { codigo: termo } : { descricao: termo });
            setOpcoesNcm(dedupPorCodigo([...(ncmSelecionado ? [ncmSelecionado] : []), ...resultado, ...NCMS_SUGERIDOS]));
            setBuscandoNcm(false);
        }, 400);
    };

    // ── CFOP ──
    const cfopSelecionado: ICfop | null = useMemo(() => {
        if (!impostoAtual.cfop) return null;
        return opcoesCfop.find((o) => o.codigo === impostoAtual.cfop) ?? { codigo: impostoAtual.cfop, descricao: "" };
    }, [impostoAtual.cfop, opcoesCfop]);

    const opcoesCfopExibidas: ICfop[] = useMemo(
        () => dedupPorCodigo([...(cfopSelecionado ? [cfopSelecionado] : []), ...opcoesCfop]),
        [cfopSelecionado, opcoesCfop]
    );

    const handleInputCfop = (_event: React.SyntheticEvent, valor: string) => {
        setInputCfop(valor);
        if (debounceCfopRef.current) clearTimeout(debounceCfopRef.current);

        const termo = valor.trim();
        if (termo.length < 2) {
            setOpcoesCfop(dedupPorCodigo([...CFOPS_SUGERIDOS, ...(cfopSelecionado ? [cfopSelecionado] : [])]));
            return;
        }

        const somenteDigitos = /^\d+$/.test(termo);
        setBuscandoCfop(true);
        debounceCfopRef.current = setTimeout(async () => {
            const resultado = await buscarCfops(somenteDigitos ? { codigo: termo } : { descricao: termo });
            setOpcoesCfop(dedupPorCodigo([...(cfopSelecionado ? [cfopSelecionado] : []), ...resultado, ...CFOPS_SUGERIDOS]));
            setBuscandoCfop(false);
        }, 400);
    };

    return (
        <>
            <Autocomplete
                options={opcoesNcmExibidas}
                value={ncmSelecionado}
                filterOptions={(x) => x}
                inputValue={inputNcm}
                onInputChange={handleInputNcm}
                onChange={(_e, valor) => atualizar("ncm", valor ? valor.codigo : "")}
                isOptionEqualToValue={(opcao, valor) => opcao.codigo === valor.codigo}
                getOptionLabel={(opcao) => (opcao.descricao ? `${opcao.codigo} — ${opcao.descricao}` : opcao.codigo)}
                renderOption={(props, opcao) => {
                    const { key, ...rest } = props as any;
                    return (
                        <li {...rest} key={opcao.codigo}>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="body2"><b>{opcao.codigo}</b></Typography>
                                {opcao.descricao && (
                                    <Typography variant="caption" color="text.secondary">{opcao.descricao}</Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                loading={buscandoNcm}
                noOptionsText="Digite um código ou descrição para buscar na Focus NFe"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="NCM"
                        helperText="Busque por código ou descrição (ex: pizza, refeição, 2106...)"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {buscandoNcm ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <Autocomplete
                sx={{ marginTop: "1.5rem" }}
                options={opcoesCfopExibidas}
                value={cfopSelecionado}
                filterOptions={(x) => x}
                inputValue={inputCfop}
                onInputChange={handleInputCfop}
                onChange={(_e, valor) => atualizar("cfop", valor ? valor.codigo : "")}
                isOptionEqualToValue={(opcao, valor) => opcao.codigo === valor.codigo}
                getOptionLabel={(opcao) => (opcao.descricao ? `${opcao.codigo} — ${opcao.descricao}` : opcao.codigo)}
                renderOption={(props, opcao) => {
                    const { key, ...rest } = props as any;
                    return (
                        <li {...rest} key={opcao.codigo}>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="body2"><b>{opcao.codigo}</b></Typography>
                                {opcao.descricao && (
                                    <Typography variant="caption" color="text.secondary">{opcao.descricao}</Typography>
                                )}
                            </Box>
                        </li>
                    );
                }}
                loading={buscandoCfop}
                noOptionsText="Digite um código ou descrição para buscar na Focus NFe"
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="CFOP"
                        helperText="Busque por código ou descrição (ex: venda, 5101...)"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {buscandoCfop ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <FormControl fullWidth sx={{ marginTop: "1.5rem" }}>
                <InputLabel id="icms-origem-label">Origem da mercadoria (ICMS)</InputLabel>
                <Select
                    labelId="icms-origem-label"
                    label="Origem da mercadoria (ICMS)"
                    value={impostoAtual.icmsOrigem ?? ""}
                    onChange={(e) => atualizar("icmsOrigem", e.target.value)}
                >
                    {OPCOES_ICMS_ORIGEM.map((opcao) => (
                        <MenuItem key={opcao.valor} value={opcao.valor}>{opcao.descricao}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth sx={{ marginTop: "1.5rem" }}>
                <InputLabel id="icms-modalidade-label">Modalidade da base de cálculo (ICMS)</InputLabel>
                <Select
                    labelId="icms-modalidade-label"
                    label="Modalidade da base de cálculo (ICMS)"
                    value={impostoAtual.icmsModalidadeBaseCalculo ?? ""}
                    onChange={(e) => atualizar("icmsModalidadeBaseCalculo", e.target.value)}
                >
                    {OPCOES_ICMS_MODALIDADE_BASE_CALCULO.map((opcao) => (
                        <MenuItem key={opcao.valor} value={opcao.valor}>{opcao.descricao}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                <TextField
                    fullWidth
                    label="Alíquota ICMS (%)"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={impostoAtual.icmsAliquota ?? ""}
                    onChange={(e) => atualizar("icmsAliquota", e.target.value === "" ? undefined : Number(e.target.value))}
                />
                <TextField
                    fullWidth
                    label="Base de cálculo ICMS (R$)"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={impostoAtual.icmsBaseCalculo ?? ""}
                    onChange={(e) => atualizar("icmsBaseCalculo", e.target.value === "" ? undefined : Number(e.target.value))}
                />
            </Box>
        </>
    );
};
